import * as Sentry from "@sentry/react";
import { browserTracingIntegration } from "@sentry/react";
import posthog, { PostHog } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import React from "react";
import { createRoot } from "react-dom/client";
import { ToastContainer } from "react-toastify";
import CommunityMatchmakingScreenComponent from "./components/pages/community/CommunityMatchmakingScreen";
import GiftLandingPage from "./components/pages/gift/GiftLandingPage";
import GiftCheckoutSuccess from "./components/pages/gift/GiftCheckoutSuccess";
import GiftCheckoutCancel from "./components/pages/gift/GiftCheckoutCancel";
import "./index.scss";
import reportWebVitals from "./reportWebVitals";
import { GlobalContextProvider } from "./util/context";
import { PubSubProvider } from "./util/pubsub";
import "./web-index.css";

type ReactDataInput = {
  api_base_path: string | null;
  posthog_enabled: boolean | null;
  posthog_api_key: string | null;
  posthog_host: string | null;
  sentry_enabled: boolean | null;
  sentry_dsn: string | null;
  static_asset_domain: string | null;
};

type UserInfo = {
  guid: string;
  username: string;
  name: string;
  email: string;
};

// interface AppProps {
//   eventGuid: string;
// }

// const EventPortal = ({ eventGuid }: AppProps) => (
//   <div>
//     <h1>Hello, Web App!</h1>
//     {eventGuid && <p>The event GUID is: {eventGuid}</p>}
//   </div>
// );

const getUserInfo = (): UserInfo | null => {
  const userInfoElement = document.getElementById("user-info");
  if (!userInfoElement) {
    return null;
  }
  return JSON.parse(userInfoElement.textContent!);
};

const setupAnalytics = (data: ReactDataInput) => {
  if (data.posthog_enabled) {
    // eslint-disable-next-line no-console
    console.log("Setting up Posthog...");
    posthog.init(data.posthog_api_key!, {
      api_host: data.posthog_host!,
      loaded: (ph: PostHog) => {
        const userInfo = getUserInfo();
        if (!userInfo) {
          return;
        }
        ph.identify(userInfo.guid);
      },
    });
  } else {
    // eslint-disable-next-line no-console
    console.log("Posthog not enabled");
  }
  if (data.sentry_enabled === true) {
    // eslint-disable-next-line no-console
    console.log("Setting up Sentry...");
    Sentry.init({
      dsn: data.sentry_dsn!,
      integrations: [browserTracingIntegration],
      tracesSampleRate: 1.0,
    });
  } else {
    // eslint-disable-next-line no-console
    console.log("Sentry not enabled");
  }
};

// Elements referenced in elementMap are ones that are effectively "exported" from the
// JS bundle and able to be mounted via Django into HTML pages.

const elementMap: any = {
  // EventPortal,
  CommunityMatchmakingScreenComponent,
  GiftLandingPage,
  GiftCheckoutSuccess,
  GiftCheckoutCancel,
};

const renderReactComponent = (
  container: HTMLElement,
  config: ReactDataInput | null,
) => {
  // The first child of the container is the element we want to render into

  const placement = container.children[0];

  // The second child of the container is the props for the component

  let props: any = {};
  if (container.children[1].textContent !== "") {
    props = JSON.parse(container.children[1].textContent!);
  }

  // Render the component

  const componentType = (placement as HTMLDivElement).dataset.component;
  const root = createRoot(placement);
  if (!elementMap[componentType!]) {
    throw new Error("Component type not found!");
  }
  const element = React.createElement(elementMap[componentType!], props, null);
  let toWrap = <PubSubProvider>{element}</PubSubProvider>;
  if (config?.posthog_enabled === true) {
    // eslint-disable-next-line no-console
    console.log("Wrapping with Posthog provider...");
    toWrap = <PostHogProvider client={posthog}>{toWrap}</PostHogProvider>;
  }
  if (config?.sentry_enabled === true) {
    // eslint-disable-next-line no-console
    console.log("Wrapping with Sentry error boundary...");
    toWrap = (
      <Sentry.ErrorBoundary fallback={<div>An error has occurred</div>}>
        {toWrap}
      </Sentry.ErrorBoundary>
    );
  }
  root.render(<GlobalContextProvider>{toWrap}</GlobalContextProvider>);
};

document.addEventListener("DOMContentLoaded", () => {
  // Pull any React data input that is found on the page

  let reactData: ReactDataInput | null = null;
  const reactDataContainer = document.getElementById("react_input_data");
  if (reactDataContainer) {
    reactData = JSON.parse(reactDataContainer.innerText);
  }

  // Update global state as needed

  if (reactData) {
    // eslint-disable-next-line no-console
    console.log("Setting API base path to ", reactData.api_base_path);
    setupAnalytics(reactData);
  }

  // Render toaster on the page as needed

  const element = document.getElementById("toaster");
  if (element !== null) {
    const root = createRoot(element);
    root.render(
      <div>
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>,
    );
  }

  // Render any React components on the page

  const reactElements = document.querySelectorAll(".react-component-container");
  for (let i = 0; i < reactElements.length; i += 1) {
    renderReactComponent(reactElements[i] as HTMLElement, reactData);
  }
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
